<template>
  <div class="container-like-w" id="binds-mobile-matrixlike">
    <div class="content-like" ref="contentContainer">
      <div
        v-for="(item, idx) in data.question.subjects"
        :key="item._id || idx"
        class="section-matrix-like"
      >
        <!-- Image Section -->
        <div
          v-if="shouldShowImage(idx)"
          class="image-container"
        >
          <image-viewer
            :image="data.question.imageOnTitle[idx]"
            position="middle"
            :question-type="data.question.type"
            :show-image-on-title="data.question.showImageOnTitle"
          />
        </div>

        <!-- Label Section -->
        <div
          v-if="!data.question.showImageOnTitle || (!data.question.hideAllSubjectLabels && data.question.showImageOnTitle)"
          class="section-label"
        >
          <span
            :style="{ color: data.response_font_color || data.texts }"
            v-html="replaceMeta(item.label)"
          ></span>
        </div>

        <!-- Rating Section -->
        <div class="section-rating">
          <div class="content-rating">
            <div class="rating-buttons">
              <button
                class="rating-button dislike"
                :class="{ active: isSelected(item._id, 0) }"
                @click="handleRating(0, item._id)"
                :aria-label="'Dislike ' + item.label"
              >
                <i class="material-icons">thumb_down</i>
              </button>
              <button
                class="rating-button like"
                :class="{ active: isSelected(item._id, 100) }"
                @click="handleRating(100, item._id)"
                :aria-label="'Like ' + item.label"
              >
                <i class="material-icons">thumb_up</i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit Button -->
    <div
      v-if="buttonEnabled"
      class="submit-options-matrix"
      style="text-align: center;"
    >
      <app-button
        :widget="true"
        :binds-ripple="false"
        class="binds-raised binds-primary"
        :style="{
          'box-shadow': 'none',
          'background': 'none',
          'color': data.buttons
        }"
        @click="postResponse"
      >
        {{ data.question.nextLabel }}
      </app-button>
    </div>
  </div>
</template>

<script>
import { widgetService } from '@/_services'
import { replaceMetadata, formatObject } from '../../_helpers'
import ImageViewer from '@/components/common/ImageViewer.vue'

export default {
  name: 'MatrixLike',

  components: {
    'app-button': () => import('@binds-tech/binds-design-system/src/components/Button/Button'),
    ImageViewer
  },

  props: {
    data: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      value: [],
      buttonEnabled: false
    }
  },

  computed: {
    isQuestionRequired () {
      return this.data.question.required === true
    }
  },

  methods: {
    // Utility Methods
    replaceMeta (str) {
      const metadata = formatObject({
        ...JSON.parse(this.data.from),
        ...JSON.parse(this.data.metadata)
      })
      return replaceMetadata(str, metadata, this)
    },

    shouldShowImage (idx) {
      return (
        this.data.question.imageOnTitle &&
        this.data.question.imageOnTitle.length > idx &&
        this.data.question.showImageOnTitle
      )
    },

    // Rating Methods
    isSelected (id, rating) {
      return this.value.some(v => v._id === id && v.rating === rating)
    },

    handleRating (rating, id) {
      this.updateRating(rating, id)
      this.updateButtonState()
      this.scrollToNextUnanswered(id)
    },

    updateRating (rating, id) {
      const indexOption = this.value.findIndex(item => item._id === id)

      if (indexOption > -1) {
        if (this.value[indexOption].rating === rating) {
          this.value.splice(indexOption, 1)
        } else {
          this.value[indexOption].rating = rating
        }
      } else {
        this.value.push({ _id: id, rating })
      }
    },

    updateButtonState () {
      if (this.isQuestionRequired) {
        this.buttonEnabled = this.value.length === this.data.question.subjects.length
      } else {
        this.buttonEnabled = this.value.length > 0
      }
    },

    // Scroll Methods
    scrollToNextUnanswered (currentId) {
      this.$nextTick(() => {
        const container = this.$refs.contentContainer
        if (!container) return

        const nextIndex = this.findNextUnansweredIndex(currentId)

        if (nextIndex !== -1) {
          this.scrollToElement(container, `.section-matrix-like:nth-child(${nextIndex + 1})`)
        } else if (this.buttonEnabled) {
          this.scrollToElement(container, '.submit-options-matrix')
        }
      })
    },

    findNextUnansweredIndex (currentId) {
      const currentIndex = this.data.question.subjects.findIndex(
        subject => subject._id === currentId
      )

      // Look forward
      for (let i = currentIndex + 1; i < this.data.question.subjects.length; i++) {
        if (!this.value.some(v => v._id === this.data.question.subjects[i]._id)) {
          return i
        }
      }

      // Look backward
      for (let i = 0; i < currentIndex; i++) {
        if (!this.value.some(v => v._id === this.data.question.subjects[i]._id)) {
          return i
        }
      }

      return -1
    },

    scrollToElement (container, selector) {
      const element = container.querySelector(selector)
      if (!element) return

      const containerRect = container.getBoundingClientRect()
      const elementRect = element.getBoundingClientRect()
      const scrollTop = elementRect.top - containerRect.top + container.scrollTop - 20

      container.scrollTo({
        top: selector === '.submit-options-matrix' ? container.scrollHeight : scrollTop,
        behavior: 'smooth'
      })
    },

    // Response Methods
    async postResponse () {
      if (!this.data.is_to_post) return

      this.$root.$emit('setLoading', true)

      const dataToSend = this.value.length > 0
        ? { value: this.value, sending: this.data.sending }
        : { skip: true, sending: this.data.sending }

      const questionUpdated = await widgetService.checkIfNextOrFirstQuestion(
        this.data,
        dataToSend
      )

      this.$root.$emit('questionUpdated', questionUpdated)
    }
  },

  mounted () {
    this.buttonEnabled = false

    if (this.$refs.contentContainer) {
      this.$refs.contentContainer.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }
  }
}
</script>

<style lang="scss">
.container-like-w {
  width: 95%;
  min-height: 250px;
  max-height: 420px;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;

  .content-like {
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    min-height: 100%;
    max-height: 500px;
    margin: 10px 0;
    scrollbar-width: thin;
    scrollbar-color: #888 #f1f1f1;

    &::-webkit-scrollbar {
      width: 3px;

      &-track {
        background: #f1f1f1;
      }

      &-thumb {
        background: #888;

        &:hover {
          background: #555;
        }
      }
    }

    .section-matrix-like {
      border: 1px solid #e0e0e0;
      border-radius: 5px;
      padding: 15px;
      width: 97%;
      min-height: 50px;
      margin-bottom: 10px;
      display: flex;
      flex-direction: column;
      align-items: center;

      .image-container {
        /* width: 200px;
        height: 200px;
        margin-bottom: 15px;
        display: flex;
        align-items: center;
        justify-content: center;

        :deep(.image-viewer-container) {
          width: 100%;
          height: 100%;
          margin: 0;
        } */
      }

      .section-label {
        width: 100%;
        text-align: center;
        margin-bottom: 15px;

        span {
          overflow-wrap: break-word;
        }
      }

      .section-rating {
        width: 100%;
        height: 52px;
        display: flex;
        align-items: center;
        justify-content: center;

        .content-rating {
          position: relative;
          height: 80%;
          width: 180px;
          border-radius: 35px;
          box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.26);
          border: 1px solid #cacaca;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;

          .rating-buttons {
            display: flex;
            width: 100%;
            height: 100%;

            .rating-button {
              width: 50%;
              height: 100%;
              border: none;
              background: transparent;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: background-color 0.2s;

              i {
                font-size: 24px;
              }

              &.dislike {
                border-radius: 35px 0 0 35px;
                border-right: 1px solid #cacaca;

                i {
                  color: #ea4922;
                }

                &:hover,
                &.active {
                  background: #ea492240;
                }
              }

              &.like {
                border-radius: 0 35px 35px 0;

                i {
                  color: #51ad59;
                }

                &:hover,
                &.active {
                  background: #51ad5940;
                }
              }
            }
          }
        }
      }
    }
  }
}

@media screen and (max-width: 430px), (max-height: 900px) {
  #binds-mobile-matrixlike {
    .content-like {
      min-height: 320px;
      max-height: 320px;
    }
  }
}

@media screen and (max-width: 360px), (max-height: 800px) {
  #binds-mobile-matrixlike {
    font-size: 13px;

    &.container-like-w .content-like .section-matrix-like {
      width: 96%;
    }
  }
}
</style>
